import {
  acceptInvite,
  getUser,
  handleAuthCallback,
  login as identityLogin,
  logout as identityLogout,
  onAuthChange,
  requestPasswordRecovery,
} from "@netlify/identity";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, demoMode } from "../lib/api";
import type { SessionUser } from "../types";

interface AuthContextValue {
  user: SessionUser | null;
  loading: boolean;
  inviteToken: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  recover: (email: string) => Promise<void>;
  acceptInvite: (password: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const demoUser: SessionUser = { id: "demo-owner", email: "weihang@example.com", name: "Weihang", role: "owner" };

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(demoMode ? demoUser : null);
  const [loading, setLoading] = useState(!demoMode);
  const [inviteToken, setInviteToken] = useState<string | null>(null);

  const hydrate = useCallback(async () => {
    if (demoMode) return;
    const current = await getUser();
    if (!current) { setUser(null); return; }
    const session = await api<{ user: SessionUser }>("/api/me");
    setUser(session.user);
  }, []);

  useEffect(() => {
    if (demoMode) return;
    let mounted = true;
    void (async () => {
      try {
        const callback = await handleAuthCallback() as unknown as { type?: string; token?: string } | null;
        if (callback?.type === "invite" && callback.token) setInviteToken(callback.token);
        await hydrate();
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    const unsubscribe = onAuthChange(() => { void hydrate(); });
    return () => { mounted = false; unsubscribe(); };
  }, [hydrate]);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    inviteToken,
    login: async (email, password) => { await identityLogin(email, password); await hydrate(); },
    logout: async () => { if (!demoMode) await identityLogout(); setUser(null); },
    recover: async (email) => requestPasswordRecovery(email),
    acceptInvite: async (password) => {
      if (!inviteToken) return;
      await acceptInvite(inviteToken, password);
      setInviteToken(null);
      await hydrate();
    },
  }), [user, loading, inviteToken, hydrate]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
